import { SKILL_FORMAT_LIMITS } from '@parcel/types';
import { type SkillError, parseSkillError } from '../../lib/spec-skills.js';

/** Human copy for the reason codes the skill service emits; unknown reasons fall back. */
const REASON_COPY: Readonly<Record<string, string>> = {
  'frontmatter.missing':
    'SKILL.md must start with a YAML frontmatter block that names the skill and describes it.',
  'frontmatter.unterminated': 'The frontmatter block is missing its closing --- line.',
  'frontmatter.multiple_documents': 'The frontmatter holds more than one YAML document.',
  'frontmatter.not_a_map': 'The frontmatter must be a set of key: value lines.',
  'frontmatter.parse_error': 'The frontmatter is not valid YAML.',
  'frontmatter.alias': 'YAML anchors and aliases are not allowed in the frontmatter.',
  'frontmatter.name_missing': 'Give the skill a name in the frontmatter.',
  'frontmatter.name_invalid':
    'The name must be lowercase letters, numbers and single hyphens, like weekly-pipeline-review.',
  'frontmatter.name_length': `The name is at most ${SKILL_FORMAT_LIMITS.nameChars} characters.`,
  'frontmatter.description_missing': 'Give the skill a description in the frontmatter.',
  'frontmatter.description_invalid': 'The description must be a non-empty line of text.',
  'frontmatter.description_length': `The description is at most ${SKILL_FORMAT_LIMITS.descriptionChars} characters.`,
  'frontmatter.compatibility_invalid': 'The compatibility field must be a line of text.',
  'frontmatter.compatibility_length': `The compatibility field is at most ${SKILL_FORMAT_LIMITS.compatibilityChars} characters.`,
  'frontmatter.license_invalid': 'The license field must be a line of text.',
  'frontmatter.metadata_invalid': 'The metadata field must be a map of simple values.',
  'frontmatter.metadata_reserved': 'The metadata field uses a key Parcel reserves.',
  'frontmatter.metadata_bytes': `The metadata field is at most ${SKILL_FORMAT_LIMITS.metadataBytes} bytes.`,
  'frontmatter.unknown_key':
    'The frontmatter has a key Parcel does not read. Only name, description, license, compatibility and metadata are allowed.',
  'frontmatter.host_field':
    'The frontmatter names a host setting, like hooks or commands, that skills cannot carry.',
  'content.nul_byte': 'A file contains binary content.',
  'content.lone_surrogate': 'A file contains text that is not valid UTF-8.',
  'content.control_character': 'A file contains control characters.',
  'content.shebang': 'A file starts with a script shebang, which skills cannot carry.',
  'bundle.empty': 'Add at least SKILL.md before saving.',
  'bundle.root_missing': 'The bundle needs a SKILL.md file.',
  'bundle.root_duplicate': 'The bundle has more than one SKILL.md file.',
  'bundle.files': `A skill holds at most ${SKILL_FORMAT_LIMITS.files} files.`,
  'bundle.bytes': `The whole skill is at most ${SKILL_FORMAT_LIMITS.bundleBytes} bytes.`,
  'bundle.schema': 'One of the files is not shaped the way the API expects.',
  'path.absolute': 'File paths must be relative, with no leading slash.',
  'path.backslash': 'File paths use forward slashes.',
  'path.control_character': 'A file path contains control characters.',
  'path.length': `A file path is at most ${SKILL_FORMAT_LIMITS.pathChars} characters.`,
  'path.depth': `A file path is at most ${SKILL_FORMAT_LIMITS.pathDepth} folders deep.`,
  'path.empty_segment': 'A file path has an empty folder segment.',
  'path.traversal': 'A file path cannot climb out of the bundle with ..',
  'path.hidden_segment': 'Hidden files and folders, starting with a dot, are not allowed.',
  'path.duplicate': 'Two files share the same path.',
  'path.root_conflict': 'Only the root SKILL.md may use that name.',
  'path.unsupported_extension': 'Only Markdown, plain text and JSON files can be part of a skill.',
  'media_type.mismatch': 'A file’s media type does not match its extension.',
  'json.invalid': 'A JSON file does not parse.',
  'json.host_manifest': 'A JSON file looks like a plugin manifest, which skills cannot carry.',
  'reference.missing_file': 'SKILL.md links to a file that is not in the bundle.',
  reserved_slug: 'That name is reserved for a built-in Parcel skill. Pick another.',
  slug_taken: 'A skill with that name already exists here. Pick another name.',
  skill_writes_off: 'Skill editing is switched off right now.',
  skill_loading_off: 'Skills are switched off right now.',
  stale_draft_version: 'Someone else saved this draft after you opened it.',
  stale_version: 'This skill changed after you opened it.',
  maintainer_required: 'Only the maintainer, an owner or an admin can change this skill.',
  personal_denied: 'Only its creator can see or change a personal skill.',
  skill_forbidden: 'You do not have access to this skill.',
  official_read_only: 'Official skills are read-only. Fork one to change it.',
  official_not_installable: 'This official skill cannot be installed in Parcel.',
  skill_not_found: 'That skill no longer exists.',
  skill_deleted: 'That skill was deleted.',
  official_skill_not_found: 'This skill is no longer in the Parcel catalog.',
  pinned_release_unavailable: 'The version you added is no longer available.',
  official_source_unavailable: 'Official skills are unavailable right now.',
  official_source_not_populated: 'Official skills are unavailable right now.',
};

export interface SkillIssue {
  path: string | null;
  message: string;
}

/** Human copy for a skill error, or the generic line when nothing better is known. */
export function skillErrorMessage(error: SkillError): string {
  return REASON_COPY[error.reason] ?? error.message ?? 'The workspace refused the request.';
}

/** The human copy for a known reason only; never the raw API message. */
export function knownSkillErrorText(error: unknown): string | null {
  const parsed = parseSkillError(error);
  return parsed ? (REASON_COPY[parsed.reason] ?? null) : null;
}

/** One or two issues to show under an editor, from any thrown value. */
export function skillIssuesFromError(error: unknown): SkillIssue[] {
  const parsed = parseSkillError(error);
  if (!parsed) return [{ path: null, message: 'The workspace refused the request.' }];
  const path = typeof parsed.details.path === 'string' ? parsed.details.path : null;
  return [{ path, message: skillErrorMessage(parsed) }];
}

/** A single line for a toast or a banner. */
export function skillErrorText(error: unknown): string {
  return skillIssuesFromError(error)
    .map((issue) => (issue.path ? `${issue.message} (${issue.path})` : issue.message))
    .join(' ');
}
